import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getApiBase } from "../lib/apiBase";

function MeetForm() {
  const { meetId } = useParams();
  const isEdit = Boolean(meetId);
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [link, setLink] = useState("");
  const [athletes, setAthletes] = useState([]);
  const [fetchStatus, setFetchStatus] = useState("");
  const [lastFetchedUrl, setLastFetchedUrl] = useState("");
  const [expandedAthletes, setExpandedAthletes] = useState({});
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [isLoading, setIsLoading] = useState(isEdit);
  const [initialState, setInitialState] = useState({
    name: "",
    date: "",
    link: "",
    athletes: [],
  });
  const navigate = useNavigate();
  const apiBase = useMemo(() => getApiBase(), []);
  const importInputRef = useRef(null);

  const normalizeOpenPowerliftingId = useCallback((value) => {
    if (!value) return "";
    return value.toLowerCase().replace(/[^a-z0-9]/g, "");
  }, []);

  const getAthleteName = useCallback((athlete) => {
    if (athlete && typeof athlete === "object") {
      return athlete.name || athlete.full_name || athlete.fullName || "";
    }
    return "";
  }, []);

  const getOpenPowerliftingId = useCallback((athlete) => {
    if (athlete && typeof athlete === "object") {
      return athlete.openpowerlifting_id || athlete.openPowerliftingId || "";
    }
    return "";
  }, []);

  const hasAthleteStats = useCallback((athlete) => {
    if (!athlete || typeof athlete !== "object") return false;
    const stats = athlete.stats;
    if (!stats) return false;
    if (Array.isArray(stats)) return stats.length > 0;
    if (typeof stats === "string") return stats.trim().length > 0;
    if (typeof stats === "object") return Object.keys(stats).length > 0;
    return false;
  }, []);

  const getAthleteStat = useCallback((athlete, key) => {
    if (!athlete || typeof athlete !== "object") return "";
    const stats = athlete.stats;
    if (!stats || typeof stats !== "object") return "";
    const value = stats[key];
    if (value === undefined || value === null) return "";
    return String(value);
  }, []);

  const handleAthleteStatChange = (index, key, value) => {
    setAthletes((prev) =>
      prev.map((entry, idx) => {
        if (idx !== index) return entry;
        const existing = entry && typeof entry === "object" ? { ...entry } : {};
        const nextStats =
          existing.stats && typeof existing.stats === "object"
            ? { ...existing.stats }
            : {};
        if (value === "") {
          delete nextStats[key];
        } else {
          nextStats[key] = value;
        }
        if (Object.keys(nextStats).length) {
          existing.stats = nextStats;
        } else {
          delete existing.stats;
        }
        return existing;
      })
    );
  };

  const normalizeAthleteList = useCallback(
    (items) => {
      if (!Array.isArray(items)) return [];
      return items
        .map((item) => {
          if (!item || typeof item !== "object") return null;
          const name = getAthleteName(item).trim();
          if (!name) return null;
          const inferredId = normalizeOpenPowerliftingId(name);
          const openPowerliftingId = normalizeOpenPowerliftingId(
            item.openpowerlifting_id || item.openPowerliftingId || ""
          );
          return {
            ...item,
            name,
            openpowerlifting_id: openPowerliftingId || inferredId,
          };
        })
        .filter(Boolean);
    },
    [getAthleteName, normalizeOpenPowerliftingId]
  );

  const isDirty = useMemo(() => {
    const normalizedInitial = {
      name: initialState.name.trim(),
      date: initialState.date || "",
      link: initialState.link.trim(),
      athletes: initialState.athletes.map((athlete) =>
        getAthleteName(athlete).trim()
      ),
    };
    const normalizedCurrent = {
      name: name.trim(),
      date: date || "",
      link: link.trim(),
      athletes: athletes.map((athlete) => getAthleteName(athlete).trim()),
    };

    if (
      normalizedInitial.name !== normalizedCurrent.name ||
      normalizedInitial.date !== normalizedCurrent.date ||
      normalizedInitial.link !== normalizedCurrent.link ||
      normalizedInitial.athletes.length !== normalizedCurrent.athletes.length
    ) {
      return true;
    }

    return normalizedInitial.athletes.some(
      (athlete, index) => athlete !== normalizedCurrent.athletes[index]
    );
  }, [athletes, date, getAthleteName, initialState, link, name]);

  useEffect(() => {
    let isActive = true;

    if (!isEdit) {
      setInitialState({ name: "", date: "", link: "", athletes: [] });
      setIsLoading(false);
      return () => {
        isActive = false;
      };
    }

    const loadMeet = async () => {
      setIsLoading(true);
      setError("");
      try {
        const response = await fetch(`${apiBase}/meet/${meetId}`, {
          method: "GET",
          credentials: "include",
        });

        if (response.status === 401) {
          navigate("/login", { replace: true });
          return;
        }

        const data = await response.json();
        if (!response.ok || data?.error) {
          throw new Error(data?.error || "Failed to load meet.");
        }

        if (isActive) {
          const meet = data.meet || {};
          const loaded = {
            name: meet.name || "",
            date: meet.date || "",
            link: meet.link || "",
            athletes: normalizeAthleteList(meet.athletes),
          };
          setName(loaded.name);
          setDate(loaded.date);
          setLink(loaded.link);
          setLastFetchedUrl(meet.link || "");
          setAthletes(loaded.athletes);
          setInitialState(loaded);
        }
      } catch (err) {
        if (isActive) {
          setError(err?.message || "Something went wrong.");
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadMeet();

    return () => {
      isActive = false;
    };
  }, [apiBase, isEdit, meetId, navigate, normalizeAthleteList]);

  const fetchMeetDetails = useCallback(
    async (url) => {
    if (isFetchingDetails) return;

    setIsFetchingDetails(true);
    setFetchStatus("Fetching meet details...");
    setError("");

    try {
      const response = await fetch(`${apiBase}/liftingcast/meet`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ url }),
      });

      if (response.status === 401) {
        navigate("/login", { replace: true });
        return;
      }

      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || "Failed to fetch meet details.");
      }

      const meet = data.meet || {};
      if (meet.name) setName(meet.name);
      if (meet.date) setDate(meet.date);
      if (Array.isArray(meet.athletes)) {
        setAthletes((prev) => {
          const existingIds = new Map();
          const existingStats = new Map();
          const existingMetaById = new Map();
          const existingMetaByName = new Map();

          prev.forEach((athlete) => {
            const existingName = getAthleteName(athlete).trim();
            const existingId = getOpenPowerliftingId(athlete).trim();
            const nameKey = normalizeOpenPowerliftingId(existingName);
            const existingIdKey = normalizeOpenPowerliftingId(existingId);
            const runsheet =
              athlete && typeof athlete === "object" && athlete.runsheet
                ? athlete.runsheet
                : null;
            const pinned =
              runsheet?.pinned ??
              athlete?.pinned ??
              athlete?.is_pinned;
            const targetPosition =
              runsheet?.targetPosition ??
              athlete?.targetPosition ??
              athlete?.target_position;
            const meta = {
              runsheet:
                runsheet && typeof runsheet === "object" ? runsheet : null,
              pinned,
              targetPosition,
            };

            if (existingName && existingId) {
              existingIds.set(nameKey, existingId);
            }
            if (existingIdKey && hasAthleteStats(athlete)) {
              existingStats.set(existingIdKey, athlete.stats);
            }
            if (existingIdKey && (meta.runsheet || pinned !== undefined || targetPosition !== undefined)) {
              existingMetaById.set(existingIdKey, meta);
            }
            if (nameKey && (meta.runsheet || pinned !== undefined || targetPosition !== undefined)) {
              existingMetaByName.set(nameKey, meta);
            }
          });

          const next = normalizeAthleteList(meet.athletes);
          return next.map((athlete) => {
            const athleteName = getAthleteName(athlete).trim();
            const nameKey = normalizeOpenPowerliftingId(athleteName);
            const existingId = nameKey ? existingIds.get(nameKey) : "";
            let nextAthlete = existingId
              ? { ...athlete, openpowerlifting_id: existingId }
              : { ...athlete };
            const idKey = normalizeOpenPowerliftingId(
              getOpenPowerliftingId(nextAthlete)
            );

            const meta = (idKey && existingMetaById.get(idKey)) || existingMetaByName.get(nameKey);
            if (meta) {
              const nextRunsheet = {
                ...(meta.runsheet && typeof meta.runsheet === "object"
                  ? meta.runsheet
                  : {}),
                ...(nextAthlete.runsheet &&
                typeof nextAthlete.runsheet === "object"
                  ? nextAthlete.runsheet
                  : {}),
              };
              if (meta.pinned !== undefined && nextRunsheet.pinned === undefined) {
                nextRunsheet.pinned = meta.pinned;
              }
              if (
                meta.targetPosition !== undefined &&
                nextRunsheet.targetPosition === undefined
              ) {
                nextRunsheet.targetPosition = meta.targetPosition;
              }
              if (Object.keys(nextRunsheet).length) {
                nextAthlete = { ...nextAthlete, runsheet: nextRunsheet };
              }
              if (meta.pinned !== undefined && nextAthlete.pinned === undefined) {
                nextAthlete = { ...nextAthlete, pinned: meta.pinned };
              }
              if (
                meta.targetPosition !== undefined &&
                nextAthlete.targetPosition === undefined
              ) {
                nextAthlete = { ...nextAthlete, targetPosition: meta.targetPosition };
              }
            }

            if (idKey && !hasAthleteStats(nextAthlete)) {
              const cachedStats = existingStats.get(idKey);
              if (cachedStats) {
                return { ...nextAthlete, stats: cachedStats };
              }
            }
            return nextAthlete;
          });
        });
      }
      setFetchStatus("");
    } catch (err) {
      setFetchStatus("");
      setError(err?.message || "Something went wrong.");
    } finally {
      setLastFetchedUrl(url);
      setIsFetchingDetails(false);
    }
    },
    [
      apiBase,
      getAthleteName,
      getOpenPowerliftingId,
      hasAthleteStats,
      isFetchingDetails,
      navigate,
      normalizeAthleteList,
      normalizeOpenPowerliftingId,
    ]
  );

  useEffect(() => {
    const trimmed = link.trim();
    if (!trimmed || trimmed === lastFetchedUrl) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      fetchMeetDetails(trimmed);
    }, 700);

    return () => clearTimeout(timeoutId);
  }, [fetchMeetDetails, lastFetchedUrl, link]);

  const handleAddAthlete = () => {
    setAthletes((prev) => [...prev, { name: "" }]);
  };

  const handleRemoveAthlete = (index) => {
    setAthletes((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleAthleteChange = (index, value) => {
    setAthletes((prev) =>
      prev.map((entry, idx) =>
        idx === index
          ? (() => {
              const existing =
                entry && typeof entry === "object" ? { ...entry } : {};
              const previousName = getAthleteName(existing).trim();
              const previousAuto = normalizeOpenPowerliftingId(previousName);
              const nextName = value;
              const nextAuto = normalizeOpenPowerliftingId(nextName);
              const currentId = normalizeOpenPowerliftingId(
                existing.openpowerlifting_id || ""
              );
              const shouldUpdateId =
                !currentId || currentId === previousAuto;
              return {
                ...existing,
                name: nextName,
                openpowerlifting_id: shouldUpdateId ? nextAuto : currentId,
              };
            })()
          : entry
      )
    );
  };

  const handleAthleteFieldChange = (index, field, value) => {
    const nextValue =
      field === "openpowerlifting_id"
        ? normalizeOpenPowerliftingId(value)
        : value;
    setAthletes((prev) =>
      prev.map((entry, idx) =>
        idx === index
          ? {
              ...(entry && typeof entry === "object" ? entry : {}),
              [field]: nextValue,
            }
          : entry
      )
    );
  };

  const toggleAthleteDetails = (index) => {
    setExpandedAthletes((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const buildMeetPayload = useCallback(
    (includeId = false) => {
      const trimmedAthletes = athletes
        .map((athlete) => {
          if (!athlete || typeof athlete !== "object") return null;
          const name = getAthleteName(athlete).trim();
          if (!name) return null;
          const inferredId = normalizeOpenPowerliftingId(name);
          const openPowerliftingId = normalizeOpenPowerliftingId(
            athlete.openpowerlifting_id || athlete.openPowerliftingId || ""
          );
          return {
            ...athlete,
            name,
            openpowerlifting_id: openPowerliftingId || inferredId,
          };
        })
        .filter(Boolean);
      const payload = {
        name: name.trim(),
        date,
        link: link.trim(),
        athletes: trimmedAthletes,
      };
      if (includeId && isEdit && meetId) {
        payload.id = meetId;
      }
      return payload;
    },
    [
      athletes,
      date,
      getAthleteName,
      isEdit,
      link,
      meetId,
      name,
      normalizeOpenPowerliftingId,
    ]
  );

  const handleSave = async (event) => {
    event.preventDefault();

    if (!name.trim()) {
      setError("Meet name is required.");
      setStatus("");
      return;
    }

    setIsBusy(true);
    setError("");
    setStatus("Saving meet...");

    try {
      const payload = buildMeetPayload(false);
      const response = await fetch(`${apiBase}/meet/${isEdit ? meetId : "new"}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (response.status === 401) {
        navigate("/login", { replace: true });
        return;
      }

      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || "Failed to save meet.");
      }

      setInitialState(payload);
      navigate("/", { replace: true });
    } catch (err) {
      setStatus("");
      setError(err?.message || "Something went wrong.");
    } finally {
      setIsBusy(false);
    }
  };

  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  if (isLoading) {
    return <div className="status">Loading meet...</div>;
  }

  const handleBack = () => {
    if (isDirty && !window.confirm("Discard unsaved changes?")) {
      return;
    }
    navigate("/", { replace: true });
  };

  const handleExport = () => {
    const payload = buildMeetPayload(true);
    const safeName = payload.name
      ? payload.name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "")
      : "meet";
    const stamp = new Date().toISOString().slice(0, 10);
    const filename = `${safeName || "meet"}-${stamp}.json`;
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const handleImportClick = () => {
    if (isDirty && !window.confirm("Importing will overwrite this form. Continue?")) {
      return;
    }
    importInputRef.current?.click();
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const raw = await file.text();
      const parsed = JSON.parse(raw);
      const meet =
        parsed?.meet && typeof parsed.meet === "object" ? parsed.meet : parsed;
      if (!meet || typeof meet !== "object") {
        throw new Error("Invalid meet JSON.");
      }

      const nextName = String(meet.name || "").trim();
      const nextDate = String(meet.date || "");
      const nextLink = String(meet.link || "").trim();
      const nextAthletes = normalizeAthleteList(meet.athletes);

      setName(nextName);
      setDate(nextDate);
      setLink(nextLink);
      setLastFetchedUrl(nextLink);
      setAthletes(nextAthletes);
      setExpandedAthletes({});
      setFetchStatus("");
      setStatus("Meet imported. Remember to save.");
      setError("");
    } catch (err) {
      setError(err?.message || "Failed to import meet.");
    } finally {
      event.target.value = "";
    }
  };

  const handleRefresh = () => {
    const trimmed = link.trim();
    if (!trimmed) {
      setError("Please enter a LiftingCast URL first.");
      return;
    }
    fetchMeetDetails(trimmed);
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm("Delete this meet? This cannot be undone.")) {
      return;
    }

    setIsBusy(true);
    setError("");
    setStatus("Deleting meet...");

    try {
      const response = await fetch(`${apiBase}/meet/${meetId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.status === 401) {
        navigate("/login", { replace: true });
        return;
      }

      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || "Failed to delete meet.");
      }

      navigate("/", { replace: true });
    } catch (err) {
      setStatus("");
      setError(err?.message || "Something went wrong.");
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="meet-form">
      <header className="meet-form-header">
        <button type="button" className="back-link" onClick={handleBack}>
          ←
        </button>
        <h2>Edit Meet</h2>
        <div className="meet-form-actions">
          {isEdit && meetId ? (
            <button
              type="button"
              className="link-button"
              onClick={() => navigate(`/meet/${meetId}/runsheet`)}
              disabled={isBusy || isFetchingDetails}
            >
              View run sheet
            </button>
          ) : null}
          <button
            type="submit"
            form="meet-form"
            className="save-button"
            disabled={isBusy || isFetchingDetails}
          >
            {isBusy ? "Saving..." : "Save"}
          </button>
        </div>
      </header>

      <form id="meet-form" className="form" onSubmit={handleSave}>
        <div className="form-section">
          <p className="section-title">Meet Details</p>
          <label className="field">
            <span>LiftingCast URL</span>
            <input
              type="url"
              value={link}
              onChange={(event) => setLink(event.target.value)}
              placeholder="https://liftingcast.com/..."
            />
          </label>
          <div className="field-row">
            <button
              type="button"
              className="link-button"
              onClick={handleRefresh}
              disabled={!link.trim() || isFetchingDetails}
            >
              ↻ Refresh from LiftingCast
            </button>
          </div>
          {isFetchingDetails ? (
            <div className="loading-inline">
              <span className="spinner" aria-hidden="true" />
              Fetching meet details...
            </div>
          ) : null}
          <div className="field-grid">
            <label className="field">
              <span>Date</span>
              <input
                type="date"
                value={date}
                onChange={(event) => setDate(event.target.value)}
                disabled={isFetchingDetails}
              />
            </label>
            <label className="field">
              <span>Meet name</span>
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Meet Name (e.g. USAPL Nationals)"
                disabled={isFetchingDetails}
              />
            </label>
          </div>
        </div>

        <div className="form-section">
          <div className="section-row">
            <p className="section-title">Athletes</p>
          </div>

          {athletes.length === 0 ? (
            <p className="empty-inline">No athletes added yet.</p>
          ) : (
            <div className="athlete-list">
              {athletes.map((athlete, index) => (
                <div className="athlete-row" key={`athlete-${index}`}>
                  <input
                    type="text"
                    value={getAthleteName(athlete)}
                    onChange={(event) =>
                      handleAthleteChange(index, event.target.value)
                    }
                    placeholder={`Athlete ${index + 1}`}
                    disabled={isFetchingDetails}
                  />
                  <button
                    type="button"
                    className={`icon-button ghost ${expandedAthletes[index] ? "active" : ""}`}
                    aria-label="Edit athlete"
                    onClick={() => toggleAthleteDetails(index)}
                    disabled={isFetchingDetails}
                  >
                    ✎
                  </button>
                  <button
                    type="button"
                    className="icon-button ghost"
                    aria-label="Remove athlete"
                    onClick={() => handleRemoveAthlete(index)}
                    disabled={isFetchingDetails}
                  >
                    🗑
                  </button>
                  {!expandedAthletes[index] ? (
                    <div className="athlete-pill-row">
                      {athlete.division ? (
                        <span className="pill">{athlete.division}</span>
                      ) : null}
                      {athlete.weight_class ? (
                        <span className="pill">{athlete.weight_class}</span>
                      ) : null}
                      {athlete.equipment ? (
                        <span className="pill">{athlete.equipment}</span>
                      ) : null}
                      {athlete.gender ? (
                        <span className="pill">{athlete.gender}</span>
                      ) : null}
                      {getOpenPowerliftingId(athlete) ? (
                        <a
                          className={`pill link-pill ${
                            hasAthleteStats(athlete) ? "" : "pill-error"
                          }`}
                          href={`https://www.openpowerlifting.org/u/${getOpenPowerliftingId(
                            athlete
                          )}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          OpenPL ↗
                        </a>
                      ) : null}
                    </div>
                  ) : null}
                  {expandedAthletes[index] ? (
                    <div className="athlete-details">
                      <div className="athlete-detail-grid">
                        <label className="field">
                          <span className="field-label">
                            OpenPowerlifting ID
                            {getOpenPowerliftingId(athlete) ? (
                              <a
                                className="link-icon-button"
                                href={`https://www.openpowerlifting.org/u/${getOpenPowerliftingId(
                                  athlete
                                )}`}
                                target="_blank"
                                rel="noreferrer"
                                aria-label="OpenPowerlifting profile"
                              >
                                ↗
                              </a>
                            ) : null}
                          </span>
                          <input
                            type="text"
                            value={athlete.openpowerlifting_id || ""}
                            onChange={(event) =>
                              handleAthleteFieldChange(
                                index,
                                "openpowerlifting_id",
                                event.target.value
                              )
                            }
                          />
                        </label>
                        <label className="field">
                          <span>Division</span>
                          <input
                            type="text"
                            value={athlete.division || ""}
                            onChange={(event) =>
                              handleAthleteFieldChange(
                                index,
                                "division",
                                event.target.value
                              )
                            }
                          />
                        </label>
                        <label className="field">
                          <span>Weight class</span>
                          <input
                            type="text"
                            value={athlete.weight_class || ""}
                            onChange={(event) =>
                              handleAthleteFieldChange(
                                index,
                                "weight_class",
                                event.target.value
                              )
                            }
                          />
                        </label>
                        <label className="field">
                          <span>Equipment</span>
                          <input
                            type="text"
                            value={athlete.equipment || ""}
                            onChange={(event) =>
                              handleAthleteFieldChange(
                                index,
                                "equipment",
                                event.target.value
                              )
                            }
                          />
                        </label>
                        <label className="field">
                          <span>Gender</span>
                          <input
                            type="text"
                            value={athlete.gender || ""}
                            onChange={(event) =>
                              handleAthleteFieldChange(
                                index,
                                "gender",
                                event.target.value
                              )
                            }
                          />
                        </label>
                        <label className="field">
                          <span>Best squat (kg)</span>
                          <input
                            type="number"
                            inputMode="decimal"
                            value={getAthleteStat(athlete, "Best Squat Kg")}
                            onChange={(event) =>
                              handleAthleteStatChange(
                                index,
                                "Best Squat Kg",
                                event.target.value
                              )
                            }
                          />
                        </label>
                        <label className="field">
                          <span>Best bench (kg)</span>
                          <input
                            type="number"
                            inputMode="decimal"
                            value={getAthleteStat(athlete, "Best Bench Kg")}
                            onChange={(event) =>
                              handleAthleteStatChange(
                                index,
                                "Best Bench Kg",
                                event.target.value
                              )
                            }
                          />
                        </label>
                        <label className="field">
                          <span>Best deadlift (kg)</span>
                          <input
                            type="number"
                            inputMode="decimal"
                            value={getAthleteStat(athlete, "Best Deadlift Kg")}
                            onChange={(event) =>
                              handleAthleteStatChange(
                                index,
                                "Best Deadlift Kg",
                                event.target.value
                              )
                            }
                          />
                        </label>
                        <label className="field">
                          <span>Session</span>
                          <input
                            type="text"
                            value={athlete.session ?? ""}
                            onChange={(event) =>
                              handleAthleteFieldChange(
                                index,
                                "session",
                                event.target.value
                              )
                            }
                          />
                        </label>
                        <label className="field">
                          <span>Flight</span>
                          <input
                            type="text"
                            value={athlete.flight ?? ""}
                            onChange={(event) =>
                              handleAthleteFieldChange(
                                index,
                                "flight",
                                event.target.value
                              )
                            }
                          />
                        </label>
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}

          <button
            style={{textAlign: "left"}}
            type="button"
            className="link-button"
            onClick={handleAddAthlete}
            disabled={isFetchingDetails}
          >
            + Athlete
          </button>
        </div>

        <div className="form-section danger">
          <p className="section-title">Danger Zone</p>
          <div className="danger-actions">
            {isEdit ? (
              <button
                type="button"
                className="danger-button"
                onClick={handleDelete}
                disabled={isBusy || isFetchingDetails}
              >
                Delete meet
              </button>
            ) : null}
            <button
              type="button"
              className="danger-secondary-button"
              onClick={handleExport}
              disabled={isBusy || isFetchingDetails}
            >
              Export meet
            </button>
            <button
              type="button"
              className="danger-secondary-button"
              onClick={handleImportClick}
              disabled={isBusy || isFetchingDetails}
            >
              Import meet
            </button>
            <input
              ref={importInputRef}
              type="file"
              accept="application/json"
              onChange={handleImport}
              style={{ display: "none" }}
            />
          </div>
        </div>

        {fetchStatus ? <div className="status">{fetchStatus}</div> : null}
        {status ? <div className="status">{status}</div> : null}
        {error ? <div className="error">{error}</div> : null}
      </form>
    </div>
  );
}

export default MeetForm;
